import React from 'react';
import styled from 'styled-components';
import { TableOutlined, LineChartOutlined } from '@ant-design/icons';
import JourneyIcon from '@src/icons/journey.svg';

/**
 * Props for NoData component
 */
interface NoDataProps {
  /** Type of content that has no data */
  type?: 'chart' | 'table' | 'journey' | 'patterns';
  /** Custom message to display (optional) */
  message?: string;
  /** Additional CSS class name (optional) */
  className?: string;
}

/**
 * Container for the no data state
 */
const NoDataContainer = styled.div<{
  children?: React.ReactNode;
  className?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  height: calc(100% - 50px);
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  color: #8c8c8c;
  text-align: center;
`;

/**
 * Icon wrapper with consistent styling
 */
const IconWrapper = styled.div`
  font-size: 64px;
  color: #d9d9d9;
  margin-bottom: 16px;

  .anticon {
    display: block;
  }
`;

/**
 * Message text styling
 */
const MessageText = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.5;
  max-width: 300px;
`;

/**
 * Default messages for different content types
 */
const DEFAULT_MESSAGES = {
  chart: 'No data available to generate the chart.',
  table: 'No data available to display in the table.',
  patterns: 'No data available to display in the patterns.',
  journey:
    'No data available to create User Journey. Please adjust the configuration and try again.',
} as const;

/**
 * Icons for different content types
 */
const TYPE_ICONS = {
  chart: LineChartOutlined,
  table: TableOutlined,
  journey: JourneyIcon,
  patterns: TableOutlined,
} as const;

/**
 * NoData Component
 *
 * A reusable component for displaying empty states across the application.
 * Supports different content types (chart, table, journey) with appropriate
 * icons and messages.
 *
 * @param type - The type of content that has no data
 * @param message - Custom message to display (optional)
 * @param className - Additional CSS class name (optional)
 */
const NoData: React.FC<NoDataProps> = ({ type = 'table', message, className }) => {
  const IconComponent = TYPE_ICONS[type];
  const displayMessage = message || DEFAULT_MESSAGES[type];

  return (
    <NoDataContainer className={className}>
      <IconWrapper>
        <IconComponent />
      </IconWrapper>
      <MessageText>{displayMessage}</MessageText>
    </NoDataContainer>
  );
};

export default NoData;
